import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { getMessaging, getToken, Messaging, onMessage, isSupported } from '@angular/fire/messaging';
import { SwPush } from '@angular/service-worker';

import { CallResponseObject } from '../models/call';
import { LoggerService } from './logger.service';
import { environment } from '../../../environments/current';
import { PreferenceService } from './preference.service';
import { Router } from '@angular/router';
import { take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NotifyService {
  messaging: Messaging;
  private prefAvisos: boolean = false;
  private prefNotifyTokens: string[];

  constructor(
    private logger: LoggerService,
    private matSnackBar: MatSnackBar,
    private prefService: PreferenceService,
    private router: Router,
    readonly swPush: SwPush
  ) {
    try {
      isSupported()
        .then(isSupp => {
          if (isSupp) {
            this.messaging = getMessaging();
            this.prefService.items.subscribe(prefs => {
              if (prefs && prefs.notifyTokens) {
                this.prefNotifyTokens = [...prefs.notifyTokens];
              } else {
                this.prefNotifyTokens = [];
              }
              if (prefs && prefs.avisos === true) {
                this.prefAvisos = true;
                this.getCurrentToken();
              } else if (prefs && prefs.avisos === undefined) {
                this.getCurrentToken(true);
              } else {
                this.prefAvisos = false;
              }
            });
            onMessage(this.messaging, payload => {
              this.logger.debug({ 'Notify:Messaging:New': payload });
              if (payload.data && 'gameId' in payload.data) {
                const msgRef = this.open(payload.notification['body'], 'Ir ao jogo', 0);
                msgRef
                  .onAction()
                  .pipe(take(1))
                  .subscribe(() => this.actionGame(payload.data['gameId']));
              } else {
                this.open(payload.notification['body'], 'Ok');
              }
            });
          } else {
            this.logger.info('Notify:Messaging:NoSupport');
          }
        })
        .catch(err => this.logger.error({ 'Notify:Messaging:Supported': err }));
    } catch (err) {
      this.logger.error('Notify:Messaging');
    }
  }

  open(message: string, action: string, duration?: number, className?: string[]): MatSnackBarRef<TextOnlySnackBar> {
    return this.matSnackBar.open(message, action, {
      duration: duration ? duration : 5000,
      horizontalPosition: 'center', // hPosition ? hPosition : 'center',
      verticalPosition: 'bottom', // vPosition ? vPosition : 'top',
      panelClass: className,
    });
  }

  showInfo(mensagem: string, acao = '', duration?: number) {
    this.open(mensagem, acao, duration ?? 5000, ['snack-all', 'snack-success']);
  }

  showFastInfo(mensagem: string, acao = '', duration?: number) {
    this.open(mensagem, acao, duration ?? 1000, ['snack-all', 'snack-success']);
  }

  showWarning(mensagem: string, acao = '', duration?: number) {
    this.open(mensagem, acao, duration ?? 5000, ['snack-all', 'snack-error']);
  }

  showResponse(response: CallResponseObject) {
    if (response.status == 0) {
      this.showInfo(response.message);
    } else {
      this.showWarning(response.message);
    }
  }

  backLater(): void {
    this.showWarning('Estamos passando por uma dificuldade. Tente de novo mais tarde');
  }

  requestPermission() {
    this.logger.debug('Notify:Messaging:RequestPermission');

    Promise.resolve(Notification.requestPermission()).then(permission => {
      this.logger.debug(`Notify:Messaging:RequestPermission:${permission}`);
      if (permission === 'granted') {
        return true;
      } else if (permission === 'denied') {
        this.prefService.setNotifyToken(null);
        this.showWarning(
          'Não foi possível habilitar as notificações - Vá em "Preferências" > "Avisos" para ligar novamente',
          'Fechar',
          10000
        );
        return false;
      }
    });
    return false;
  }

  async getCurrentPermission(): Promise<boolean> {
    if (await isSupported()) {
      if (this.prefAvisos && Notification.permission === 'granted') {
        return true;
      }
    }
    return false;
  }

  isNotificationSupported(): Promise<boolean> {
    return isSupported();
  }

  private getCurrentToken(updateAvisos: boolean = false): Promise<boolean> {
    return new Promise((resolve, _reject) => {
      return getToken(this.messaging, { vapidKey: environment.firebase.vapidKey })
        .then(currentToken => {
          if (currentToken) {
            this.logger.debug({ 'Notify:Messaging:Token': currentToken, Stored: this.prefNotifyTokens });
            if (!this.prefNotifyTokens.includes(currentToken)) {
              this.prefService.setNotifyToken(currentToken, updateAvisos);
            }
            resolve(true);
          } else {
            this.logger.debug('Notify:Messaging:NoToken');
            resolve(false);
          }
        })
        .catch(err => {
          this.logger.debug({ 'Notify:Messaging:Token:Error': err });
          if ('code' in err && err['code'] === 'messaging/permission-blocked') {
            this.askNotificationPermission();
          } else {
            this.logger.error({ 'Notify:Messaging:Token': err });
          }
          resolve(false);
        });
    });
  }

  private askNotificationPermission() {
    const msgRef = this.open(
      'É preciso autorizar o envio de notificações no navegador para receber avisos importantes',
      'Autorizar',
      30000
    );
    msgRef
      .onAction()
      .pipe(take(1))
      .subscribe(() => this.requestPermission());
  }

  private actionGame(gameId: string): Promise<boolean> {
    this.logger.debug({ 'Notify:Messaging:ActionGame': gameId });
    return this.router.navigate(['/jogos', gameId]);
  }
}
